import { Building, Utensils, Car, Calendar, Users, Bed, MapPin, Star, CreditCard, ClipboardList } from 'lucide-react';
import type { Product } from './types';

export const hospitalityProducts: Product[] = [
  {
    id: 'hotel-management-suite',
    title: 'Complete Hotel Management Suite',
    subtitle: 'Comprehensive hospitality operations platform',
    description: 'End-to-end hotel management system with reservation management, guest services, housekeeping coordination, and revenue optimization.',
    icon: Building,
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#hospitality', '#hotels', '#resorts', '#boutique-hotels', '#bed-breakfast', '#vacation-rentals'],
    features: [
      'Property Management System (PMS) Integration',
      'Online Booking Engine & Channel Management',
      'Guest Check-In/Check-Out Automation',
      'Housekeeping Management & Room Status Tracking',
      'Revenue Management & Dynamic Pricing',
      'Guest Communication & Service Requests',
      'Maintenance Management & Work Orders',
      'Staff Scheduling & Performance Tracking',
      'Financial Reporting & Analytics',
      'Loyalty Program Management'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'restaurant-pos-management',
    title: 'Restaurant POS & Management System',
    subtitle: 'Complete restaurant operations platform',
    description: 'Comprehensive restaurant management with POS integration, inventory control, staff management, and customer analytics.',
    icon: Utensils,
    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#restaurants', '#cafes', '#bars', '#food-service', '#quick-service', '#fine-dining'],
    features: [
      'Cloud-Based POS System & Payment Processing',
      'Menu Management & Pricing Optimisation',
      'Inventory Control & Supply Chain Management',
      'Staff Scheduling & Performance Analytics',
      'Customer Relationship Management & Loyalty',
      'Table Management & Reservation System',
      'Kitchen Display System & Order Tracking',
      'Multi-Location Management & Reporting',
      'Financial Analytics & Profit Optimisation',
      'Mobile Ordering & Delivery Integration'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-orange-500 to-red-600'
  },
  {
    id: 'travel-booking-platform',
    title: 'Travel Booking & Management Platform',
    subtitle: 'Complete travel services solution',
    description: 'Comprehensive travel management platform with booking engine, itinerary management, customer service, and business travel solutions.',
    icon: Car,
    image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#travel-agencies', '#corporate-travel', '#tour-operators', '#booking-platforms', '#travel-tech'],
    features: [
      'Multi-Service Booking Engine (Flights, Hotels, Cars)',
      'Itinerary Management & Trip Planning',
      'Customer Portal & Self-Service Options',
      'Business Travel Management & Approval Workflows',
      'Expense Tracking & Reporting Integration',
      'Supplier Integration & Rate Management',
      'Customer Communication & Support System',
      'Mobile App & Real-Time Updates',
      'Analytics & Business Intelligence',
      'Multi-Currency & Payment Processing'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'event-venue-management',
    title: 'Event & Venue Management System',
    subtitle: 'Complete event operations platform',
    description: 'Comprehensive event management with venue booking, catering coordination, guest management, and event analytics.',
    icon: Calendar,
    image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#event-venues', '#wedding-venues', '#conference-centers', '#event-planners', '#catering'],
    features: [
      'Venue Booking & Availability Management',
      'Event Planning & Timeline Coordination',
      'Guest Registration & Check-In System',
      'Catering Management & Menu Planning',
      'Vendor Management & Contract Tracking',
      'Audio/Visual Equipment & Setup Coordination',
      'Payment Processing & Invoicing',
      'Event Analytics & Performance Reporting',
      'Staff Coordination & Task Management',
      'Multi-Venue Management & Reporting'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-purple-500 to-pink-600'
  },
  {
    id: 'guest-experience-platform',
    title: 'Guest Experience Platform',
    subtitle: 'Personalized hospitality service',
    description: 'Advanced guest experience management with personalization, service automation, feedback collection, and loyalty program integration.',
    icon: Star,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#hospitality', '#guest-experience', '#personalization', '#customer-service', '#loyalty'],
    features: [
      'Guest Preference Management',
      'Personalized Service Recommendations',
      'Automated Service Requests',
      'Real-Time Feedback Collection',
      'Loyalty Program Integration',
      'Mobile Guest Services App',
      'Concierge Service Management',
      'Guest Communication Automation',
      'Experience Analytics & Insights',
      'Social Media Integration',
      'Review Management System',
      'Custom Guest Journey Mapping'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-pink-500 to-rose-600'
  },
  {
    id: 'restaurant-management-system',
    title: 'Restaurant Management System',
    subtitle: 'Complete restaurant operations solution',
    description: 'Comprehensive restaurant management system covering POS, inventory, staff management, and customer analytics for restaurants of all sizes.',
    icon: Utensils,
    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#restaurants', '#cafes', '#bars', '#food-service', '#quick-service'],
    features: [
      'Point of Sale (POS) System',
      'Inventory Management',
      'Staff Scheduling & Payroll Integration',
      'Menu Management & Pricing',
      'Customer Loyalty Programs',
      'Financial Reporting & Analytics',
      'Online Ordering Integration',
      'Table Management System',
      'Kitchen Display System',
      'Multi-Location Support'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-orange-500 to-red-600'
  },
  {
    id: 'hotel-management-system',
    title: 'Hotel Management System',
    subtitle: 'Comprehensive hotel operations platform',
    description: 'Complete hotel management solution with property management, booking engine, guest services, and revenue optimization tools.',
    icon: Building,
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#hotels', '#resorts', '#boutique-hotels', '#bed-breakfast', '#hospitality'],
    features: [
      'Property Management System (PMS)',
      'Online Booking Engine',
      'Guest Check-In/Check-Out',
      'Room Management & Housekeeping',
      'Revenue Management',
      'Guest Communication System',
      'Maintenance Management',
      'Staff Management & Scheduling',
      'Financial Reporting',
      'Channel Manager Integration'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'spa-wellness-management',
    title: 'Spa & Wellness Management',
    subtitle: 'Complete spa and wellness center solution',
    description: 'Comprehensive management system for spas, wellness centers, and beauty salons with booking, staff management, and client tracking.',
    icon: Star,
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#spa', '#wellness', '#beauty-salon', '#massage-therapy', '#fitness'],
    features: [
      'Online Booking & Scheduling',
      'Client Management & History',
      'Staff Scheduling & Commission Tracking',
      'Inventory Management for Products',
      'Treatment Package Management',
      'Payment Processing & POS',
      'Membership & Loyalty Programs',
      'Marketing & Email Campaigns',
      'Financial Reporting & Analytics',
      'Mobile App for Clients'
    ],
    pricing: { setup: 36000, monthly: 1656 },
    color: 'from-pink-500 to-purple-600'
  },
  {
    id: 'event-management-platform',
    title: 'Event Management Platform',
    subtitle: 'Complete event planning and management solution',
    description: 'Comprehensive event management system for planners, venues, and service providers with booking, coordination, and analytics.',
    icon: Calendar,
    image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#event-planning', '#wedding-venues', '#conference-centers', '#catering', '#event-services'],
    features: [
      'Event Planning & Timeline Management',
      'Venue Booking & Availability',
      'Vendor Management & Coordination',
      'Guest Registration & Check-In',
      'Budget Management & Cost Tracking',
      'Catering & Menu Planning',
      'Seating Arrangements & Floor Plans',
      'Payment Processing & Invoicing',
      'Event Analytics & Reporting',
      'Mobile Event Management App'
    ],
    pricing: { setup: 44000, monthly: 2024 },
    color: 'from-purple-500 to-indigo-600'
  },
  {
    id: 'fitness-gym-management',
    title: 'Fitness & Gym Management',
    subtitle: 'Complete fitness facility management system',
    description: 'Comprehensive gym and fitness center management with membership, class scheduling, trainer management, and health tracking.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#fitness', '#gym', '#personal-training', '#yoga-studio', '#wellness-center'],
    features: [
      'Membership Management & Billing',
      'Class Scheduling & Booking',
      'Trainer & Staff Management',
      'Equipment Maintenance Tracking',
      'Member Check-In System',
      'Health & Fitness Tracking',
      'Payment Processing & POS',
      'Marketing & Member Engagement',
      'Financial Reporting & Analytics',
      'Mobile App for Members'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-green-500 to-teal-600'
  },
  {
    id: 'customer-service-platform',
    title: 'Customer Service Platform',
    subtitle: 'Complete customer support and service solution',
    description: 'Comprehensive customer service management system with ticketing, live chat, knowledge base, and performance analytics.',
    icon: ClipboardList,
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Hospitality & Travel',
    status: 'Available',
    businessTypes: ['#customer-service', '#support', '#hospitality', '#service-industry', '#help-desk'],
    features: [
      'Multi-Channel Support (Email, Chat, Phone)',
      'Ticket Management & Routing',
      'Knowledge Base & Self-Service Portal',
      'Live Chat & Real-Time Support',
      'Customer Feedback & Surveys',
      'Performance Analytics & Reporting',
      'Team Management & Scheduling',
      'Integration with CRM Systems',
      'Automated Response & Workflows',
      'Mobile Support App'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-indigo-500 to-blue-600'
  }
];
